import { useEffect, useState } from 'react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { CheckCircle, AlertCircle, Circle, ChevronDown, HelpCircle } from 'lucide-react';

// Twitter-style animated completion icon component
export const AnimatedCompletionIcon = ({ isValid, hasError }: { isValid: boolean; hasError: boolean }) => {
  const [justCompleted, setJustCompleted] = useState(false);
  const [prevValid, setPrevValid] = useState(isValid);

  useEffect(() => {
    if (!prevValid && isValid) {
      // Just became valid - trigger Twitter-style animation
      setJustCompleted(true);
      const timer = setTimeout(() => setJustCompleted(false), 400);
      return () => clearTimeout(timer);
    }
    setPrevValid(isValid);
  }, [isValid, prevValid]);

  if (hasError) {
    return <AlertCircle className="h-4 w-4 text-red-400 transition-all duration-200" />;
  }

  if (isValid) {
    return (
      <CheckCircle
        className={`h-4 w-4 text-green-400 transition-all ${
          justCompleted ? 'scale-125 duration-300' : 'duration-200 hover:scale-110'
        }`}
        style={{
          transform: justCompleted ? 'scale(1.4)' : 'scale(1)',
          transition: justCompleted
            ? 'transform 0.4s cubic-bezier(0.68, -0.6, 0.32, 1.6)'
            : 'transform 0.2s ease-out',
        }}
      />
    );
  }

  return (
    <Circle className="h-4 w-4 text-gray-400 transition-all duration-200 hover:text-gray-300" />
  );
};

interface FormStepCollapsibleProps {
  id: string;
  title: string;
  isValid: boolean;
  hasError?: boolean;
  collapsedContent?: React.ReactNode;
  description?: React.ReactNode;
  defaultOpen?: boolean;
  icon?: 'default' | 'optional';
  titleAction?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  'data-onboarding'?: string;
}

export const FormStepCollapsible = ({
  id,
  title,
  isValid,
  hasError = false,
  collapsedContent,
  description,
  defaultOpen = true,
  icon = 'default',
  titleAction,
  children,
  className = "rounded-xl border bg-card2",
  'data-onboarding': dataOnboarding,
}: FormStepCollapsibleProps) => {
  // Individual collapsible state - must stay open if invalid
  const [isOpen, setIsOpen] = useState(defaultOpen);

  // Force open if invalid, but only for sections that default to open
  useEffect(() => {
    if (defaultOpen && !isValid) {
      setIsOpen(true);
    }
  }, [isValid, defaultOpen]);

  const handleOpenChange = (open: boolean) => {
    // Only allow closing if valid
    if (!open && !isValid) {
      return; // Don't allow closing invalid sections
    }
    setIsOpen(open);
  };

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={handleOpenChange}
      className={className}
      data-onboarding={dataOnboarding}
    >
      <CollapsibleTrigger 
        className="flex w-full items-center justify-between px-4 py-4 text-left hover:bg-white/5 focus:outline-none focus:ring-2 focus:ring-white/20 rounded-t-xl"
        id={id}
      >
        <div className="flex items-start gap-3">
          <div className="mt-1">
            {icon === 'optional' ? (
              <HelpCircle className="h-4 w-4 text-gray-400" />
            ) : (
              <AnimatedCompletionIcon isValid={isValid} hasError={hasError} />
            )}
          </div>
          <div className="flex flex-col items-start gap-1">
            <div className="flex items-center gap-2">
              <span className="text-xl font-semibold">{title}</span>
              {titleAction && (
                <div onClick={(e) => e.stopPropagation()}>
                  {titleAction}
                </div>
              )}
            </div>
            {description && (
                <div className="text-sm text-gray-400 font-normal">
                    {description}
                </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {!isOpen && collapsedContent && (
            <div className="text-sm text-gray-400">
              {collapsedContent}
            </div>
          )}
          <ChevronDown 
            className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${
              isOpen ? 'rotate-180' : ''
            }`} 
          />
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent className="px-4 pb-4 pt-2">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
};
